/**
 * Deterministische pseudo-random generator (mulberry32).
 *
 * Builders gebruiken nooit `Math.random()`: een gevel die bij elke reload een ander
 * raampatroon krijgt, is niet te reviewen en niet te testen. Alle variatie loopt via een
 * expliciete seed, zodat dezelfde seed altijd dezelfde stad oplevert.
 */
export class Rng {
  #state: number;

  constructor(seed: number) {
    this.#state = seed >>> 0;
  }

  /** Volgende waarde in [0, 1). */
  next(): number {
    this.#state = (this.#state + 0x6d2b79f5) >>> 0;
    let t = this.#state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Waarde in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Geheel getal in [min, max]. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** True met kans `probability` (0..1). */
  chance(probability: number): boolean {
    return this.next() < probability;
  }

  /** Willekeurig element uit een niet-lege lijst. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick op een lege lijst');
    return items[Math.floor(this.next() * items.length)]!;
  }
}

/** Stabiele seed uit een string, zodat je per wijk of gebouw een naam als seed kunt geven. */
export function seedFrom(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
